/**
 * @fileoverview SMS message status polling hook - polls for Communication by Dialpad messageId.
 * @agentic connections
 */

// Hooks
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  startTransition,
} from "react";
import { useMedplum } from "@medplum/react";

// Types
import type { Communication } from "@medplum/fhirtypes";
import type { UseSMSMessageStatusResult } from "@/types/domain/messaging";

/** Default poll interval in ms. @agentic connections */
const DEFAULT_POLL_INTERVAL_MS = 2000;
/** Default max poll duration in ms. @agentic connections */
const DEFAULT_MAX_POLL_DURATION_MS = 30000;

/**
 * Options for useSMSMessageStatus polling.
 * @agentic connections
 */
type UseSMSMessageStatusOptions = {
  /** Polling interval in milliseconds (default: 2000) */
  pollInterval?: number;
  /** Maximum duration to poll in milliseconds (default: 30000) */
  maxPollDuration?: number;
  /** Whether to start polling immediately (default: true when messageId provided) */
  enabled?: boolean;
};

/**
 * Polls for Communication by Dialpad messageId; returns when delivered or timeout.
 * @agentic connections
 */
export function useSMSMessageStatus(
  messageId: string | null | undefined,
  options: UseSMSMessageStatusOptions = {},
): UseSMSMessageStatusResult {
  const {
    pollInterval = DEFAULT_POLL_INTERVAL_MS,
    maxPollDuration = DEFAULT_MAX_POLL_DURATION_MS,
    enabled = true,
  } = options;

  const medplum = useMedplum();

  // State
  const [communication, setCommunication] = useState<Communication | null>(
    null,
  );
  const [isDelivered, setIsDelivered] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Refs for cleanup and tracking
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number>(0);
  const isMountedRef = useRef(true);
  const previousMessageIdRef = useRef<string | null | undefined>(undefined);

  // Clear interval only (no state updates) - safe to call in effects
  const clearPollingInterval = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  // Stop polling function - includes state update, for external use
  const stopPolling = useCallback(() => {
    clearPollingInterval();
    if (isMountedRef.current) {
      setIsPolling(false);
    }
  }, [clearPollingInterval]);

  // Query for the specific message by identifier
  const checkMessageStatus = useCallback(async () => {
    if (!messageId) return;

    try {
      // Query by the specific Dialpad messageId identifier
      // This returns exactly 0 or 1 result - very efficient!
      const results = await medplum.searchResources("Communication", {
        identifier: `dialpad:messageId|${messageId}`,
        _count: "1",
      });

      if (results.length > 0) {
        const foundMessage = results[0];

        if (isMountedRef.current) {
          setCommunication(foundMessage);
          setIsDelivered(true);
          setError(null);
        }

        // Message found - stop polling
        stopPolling();
        return;
      }

      // Check if we've exceeded max poll duration
      const elapsed = Date.now() - startTimeRef.current;
      if (elapsed >= maxPollDuration) {
        stopPolling();
      }
    } catch (err) {
      console.error("Error checking SMS message status:", err);
      if (isMountedRef.current) {
        setError(
          err instanceof Error
            ? err
            : new Error("Failed to check message status"),
        );
      }
      // Don't stop polling on transient errors
    }
  }, [medplum, messageId, maxPollDuration, stopPolling]);

  // Start/stop polling based on messageId
  useEffect(() => {
    isMountedRef.current = true;

    // Reset state when messageId changes or becomes null
    const messageIdChanged = previousMessageIdRef.current !== messageId;
    previousMessageIdRef.current = messageId;

    if (!messageId || !enabled) {
      startTransition(() => {
        setCommunication(null);
        setIsDelivered(false);
        setIsPolling(false);
        setError(null);
      });
      clearPollingInterval();
      return;
    }

    // Reset state when messageId changes to a new value
    if (messageIdChanged) {
      startTransition(() => {
        setCommunication(null);
        setIsDelivered(false);
        setError(null);
      });
    }

    // Start polling
    startTransition(() => {
      setIsPolling(true);
    });
    startTimeRef.current = Date.now();

    // Immediate first check - scheduled via setTimeout to avoid
    // synchronous setState in effect (satisfies react-hooks/set-state-in-effect)
    const immediateCheckTimeout = setTimeout(checkMessageStatus, 0);

    // Set up interval polling
    pollIntervalRef.current = setInterval(() => {
      checkMessageStatus();
    }, pollInterval);

    // Store timeout ref for cleanup
    const cleanup = () => {
      clearTimeout(immediateCheckTimeout);
      isMountedRef.current = false;
      clearPollingInterval();
    };

    // Cleanup on unmount or messageId change
    return cleanup;
  }, [
    messageId,
    enabled,
    pollInterval,
    checkMessageStatus,
    clearPollingInterval,
  ]);

  return {
    communication,
    isDelivered,
    isPolling,
    error,
    stopPolling,
  };
}
